import type { Portfolio, RiskLevel } from '@fruitful/types';

export enum RebalancerAccountFilter {
    ALL_OPEN = 'ALL_OPEN',
    BACKDOOR_ROTH_CANDIDATES = 'BACKDOOR_ROTH_CANDIDATES',
    MISSING_REBALANCER_MODEL = 'MISSING_REBALANCER_MODEL',
}

export interface RebalancerAccountRow {
    apexAccountId: string;
    accountNumber: string;
    registrationType: string;
    state: string;
    apexPortfolio: string | null;
    apexRiskLevel: string | null;
    apexRebalancerGoalId: number | null;
    backdoorRothRole: string | null;
    backdoorRothPairedApexAccountId: string | null;
    nickname: string | null;
    membershipId: string;
    userId: string;
    userFirstName: string | null;
    userLastName: string | null;
    userEmail: string;
}

export interface RebalancerAccountsByMember {
    userId: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
    accounts: RebalancerAccountRow[];
}

export interface RebalancerAccountsByMembership {
    membershipId: string;
    members: RebalancerAccountsByMember[];
}

export interface ChangeRebalancerModelRequest {
    apexAccountId: string;
    portfolio: Portfolio | null;
    riskLevel: RiskLevel | null;
}

export interface PairBackdoorRothRequest {
    tradApexAccountId: string;
    rothApexAccountId: string;
}

export interface ApexContributionSummaryRow {
    taxYear: number | null;
    /** Annual contribution limit (dollars, decimal string). */
    contributionLimit: string | null;
    /** Total regular (non-rollover) contributions year-to-date (dollars, decimal string). */
    regularAmount: string | null;
    /** Remaining regular contribution allowed (dollars, decimal string). */
    remainingContributionAllowed: string | null;
    /** Total rollover contributions (dollars, decimal string). */
    rolloverAmount: string | null;
}

export interface IraAccountContributionSummary {
    apexAccountId: string;
    accountNumber: string;
    registrationType: string;
    /** Total account equity in cents (cash + positions). */
    accountEquityCents: number;
    /** Per-tax-year contribution summaries from the Apex Ascend API. */
    contributionSummaries: ApexContributionSummaryRow[];
}

export interface BackdoorRothPreflightResponse {
    tradIra: IraAccountContributionSummary;
    rothIra: IraAccountContributionSummary;
    /**
     * Other IRA accounts the member holds (not part of this pair).
     * Relevant for pro-rata rule assessment since all Traditional IRA balances
     * across all accounts factor into the taxable portion of a Roth conversion.
     */
    otherIraAccounts: IraAccountContributionSummary[];
    proRataWarning: string;
}

// ---------------------------------------------------------------------------
// Import UTMA Account
// ---------------------------------------------------------------------------

export interface ImportApexAccountRequest {
    apexAccountId: string;
    portfolio: Portfolio;
    riskLevel: RiskLevel;
    dryRun: boolean;
}

export interface ImportPlannedAction {
    type: 'DB_INSERT' | 'DB_UPDATE' | 'API_CALL';
    table?: string;
    service?: string;
    method?: string;
    description: string;
    data: Record<string, unknown>;
}

export interface ImportValidations {
    legalNameMatch: boolean;
    addressMatch: boolean;
    legalNameDetails?: {
        apex: { givenName: string | null; familyName: string | null };
        fruitful: {
            legalFirstName: string | null;
            legalLastName: string | null;
        };
    };
}

export interface ImportMatchedUser {
    userId: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    apexLegalNaturalPersonId: string;
}

export interface ImportApexEnrollmentSummary {
    type: string;
    state: string;
}

export interface ImportApexTrustedContact {
    trustedContactId: string;
    givenName: string | null;
    familyName: string | null;
    emailAddress: string | null;
    phoneNumber: string | null;
}

export interface ImportApexAccountDetails {
    accountId: string;
    accountNumber: string;
    registrationType: string;
    state: string;
    timeHorizon: string | null;
    managed: boolean | undefined;
    enrollments: ImportApexEnrollmentSummary[];
    trustedContact: ImportApexTrustedContact | null;
}

export interface ImportApexAccountResponse {
    dryRun: boolean;
    apexAccountDetails: ImportApexAccountDetails;
    matchedUser: ImportMatchedUser;
    validations: ImportValidations;
    plannedActions: ImportPlannedAction[];
    executedResults?: ImportPlannedAction[];
}
